/**
 * components/tabs/SettingsTab.jsx
 *
 * F3 Settings — 4 sub-tabs:
 *   Account · Data · System · Users
 *
 * Account merges Preferences + Account info + Connections.
 * Data / System / Users are Owner-only (hidden for non-owner).
 * Kite status within Account is visible to all; API credentials are Owner-only.
 *
 * Sections imported from AdminTab (exported there for reuse):
 *   FiiDiiSection, EiaForecastSection, DataSourcesSection,
 *   SchedulerLogsSection, SectorAssignmentsSection, AIRunLogSection,
 *   IndexConstituentsSection
 *
 * UsersTab embedded as-is for the Users sub-tab.
 */

import React, { useState, useEffect } from 'react';
import { useUser }  from '../../context/UserContext';
import {
  SectionCard,
  FiiDiiSection,
  EiaForecastSection,
  DataSourcesSection,
  SchedulerLogsSection,
  SectorAssignmentsSection,
  AIRunLogSection,
  IndexConstituentsSection,
  DataHealthSection,
  StorageSection,
} from './AdminTab';
import UsersTab from './UsersTab';
import AccountPage from './SettingsAccountPage';   // upstox-073

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function apiFetch(path, opts = {}) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts });
}

// ── Sub-tab config ───────────────────────────────────────────────────────────
const SETTINGS_TABS = [
  { id: 'account', label: 'Account', ownerOnly: false },
  { id: 'data',    label: 'Data',    ownerOnly: true  },
  { id: 'system',  label: 'System',  ownerOnly: true  },
  { id: 'users',   label: 'Users',   ownerOnly: true  },
];

// ── Account sub-tab (upstox-073: the Claude Design handoff) ─────────────────
// Re-exported for the tests that read the server's refusal text and the date format.
export { readJson, refusalText, lastChangedText } from './RiskLimitsTable';

function AccountContent() {
  return <AccountPage />;
}

// ── Data sub-tab (owner only) ────────────────────────────────────────────────
function DataContent() {
  return (
    <div>
      <IndexConstituentsSection />
      <SectorAssignmentsSection />
      <FiiDiiSection />
      <EiaForecastSection />
      <StorageSection />
      <DataHealthSection />
    </div>
  );
}

// ── System sub-tab (owner only) ──────────────────────────────────────────────
function AppSection() {
  const [status, setStatus] = useState('idle');

  const handleForceUpdate = async () => {
    setStatus('checking');
    try {
      navigator.serviceWorker?.addEventListener('controllerchange', () => {
        setStatus('updating');
        setTimeout(() => window.location.reload(), 300);
      }, { once: true });
      const reg = await navigator.serviceWorker?.getRegistration();
      if (reg) await reg.update();
      setTimeout(() => {
        setStatus('updating');
        setTimeout(() => window.location.reload(), 300);
      }, 2500);
    } catch {
      setStatus('updating');
      setTimeout(() => window.location.reload(), 300);
    }
  };

  const label    = status === 'idle' ? '↻  Force Update' : status === 'checking' ? 'Checking…' : 'Updating…';
  const disabled = status !== 'idle';

  return (
    <SectionCard title="App" subtitle="Force reload to pick up the latest build">
      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 16 }}>
        Fetches the latest version of the app from the server — equivalent to closing
        and reopening. Use this after a new build has been deployed.
      </div>
      <button
        onClick={handleForceUpdate}
        disabled={disabled}
        style={{
          fontSize: 12, fontWeight: 600, padding: '9px 22px',
          borderRadius: 7, border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          background: disabled ? 'var(--surface-2)' : 'var(--amber-ink)',
          color: disabled ? 'var(--ink-3)' : '#fff',
          opacity: disabled ? 0.65 : 1,
          transition: 'opacity .15s, background .15s',
        }}
      >{label}</button>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        The app also auto-reloads when it returns from background after 30+ minutes.
      </div>
    </SectionCard>
  );
}

// ── AI Pipeline section ───────────────────────────────────────────────────────
const PIPELINE_STEPS = [
  {
    tab:    'news_digest',
    label:  '1 · News Digest',
    desc:   'Pass 1 — reads last 48h of archived headlines and produces a structured digest. Must run before Dashboard.',
    poll:   'news_digest',
  },
  {
    tab:    'dashboard',
    label:  '2 · Dashboard',
    desc:   'Pass 2a — reads the news digest and produces market bias + sentiment.',
    poll:   'dashboard',
  },
  {
    tab:    'nifty',
    label:  '3 · NIFTY Prediction',
    desc:   'Pass 2b — reads the digest + Dashboard bias and produces the NIFTY level forecast.',
    poll:   'nifty',
  },
];

function PipelineStep({ step }) {
  const [status,  setStatus]  = React.useState('idle'); // idle | running | done | error
  const [lastRun, setLastRun] = React.useState(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    // Load last run time on mount
    apiFetch(`/api/ai/reports/${step.poll}?days=1`)
      .then(r => r.json())
      .then(d => {
        const r = d?.reports?.[0];
        if (r?.created_at) setLastRun(r.created_at);
      })
      .catch(() => {});
  }, [step.poll]);

  const startPoll = (before) => {
    let attempts = 0;
    pollRef.current = setInterval(() => {
      attempts++;
      apiFetch(`/api/ai/reports/${step.poll}?days=1`)
        .then(r => r.json())
        .then(d => {
          const r = d?.reports?.[0];
          const ts = r?.created_at;
          if (ts && ts !== before) {
            clearInterval(pollRef.current);
            setStatus('done');
            setLastRun(ts);
          } else if (attempts >= 60) {
            clearInterval(pollRef.current);
            setStatus('error');
          }
        })
        .catch(() => {});
    }, 5000);
  };

  const handleRun = async () => {
    if (status === 'running') return;
    setStatus('running');
    const before = lastRun;
    try {
      await apiFetch(`/api/ai/generate/${step.tab}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({}),
      });
      startPoll(before);
    } catch {
      setStatus('error');
    }
  };

  React.useEffect(() => () => clearInterval(pollRef.current), []);

  const fmtTime = ts => {
    if (!ts) return null;
    try {
      return new Date(ts).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
        timeZone: 'Asia/Kolkata',
      });
    } catch { return ts; }
  };

  const btnLabel = status === 'running' ? '⏳ Running…'
    : status === 'done'    ? '✓ Done'
    : status === 'error'   ? '⚠ Error — retry'
    : '⚡ Generate';

  const btnBg = status === 'done'  ? 'var(--up-field-ink)'
    : status === 'error' ? 'var(--down-field-ink)'
    : 'var(--amber-ink)';

  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'12px 0',
      borderBottom:'.5px solid var(--hair)' }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--ink)', marginBottom:2 }}>{step.label}</div>
        <div style={{ fontSize:11, color:'var(--ink-3)', lineHeight:1.5 }}>{step.desc}</div>
        {lastRun && (
          <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:4 }}>
            Last: {fmtTime(lastRun)} IST
          </div>
        )}
      </div>
      <button
        onClick={handleRun}
        disabled={status === 'running'}
        style={{
          flexShrink:0, fontSize:11, fontWeight:600, padding:'6px 14px',
          borderRadius:6, border:'none', cursor: status === 'running' ? 'default' : 'pointer',
          background: status === 'running' ? 'var(--surface-2)' : btnBg,
          color: status === 'running' ? 'var(--ink-3)' : '#fff',
          opacity: status === 'running' ? 0.7 : 1,
          transition: 'background .15s, opacity .15s', minWidth:110,
        }}
      >{btnLabel}</button>
    </div>
  );
}

function AIPipelineSection() {
  return (
    <SectionCard title="AI Pipeline" subtitle="Trigger reports in order — Digest → Dashboard → NIFTY">
      <div style={{ fontSize:11, color:'var(--ink-3)', lineHeight:1.6, marginBottom:12 }}>
        Run these in order. Each step polls every 5 seconds and shows ✓ Done when the report
        is saved. Dashboard and NIFTY should be run after News Digest completes.
      </div>
      <div>
        {PIPELINE_STEPS.map(s => <PipelineStep key={s.tab} step={s}/>)}
      </div>
    </SectionCard>
  );
}

function SystemContent() {
  return (
    <div>
      <AIPipelineSection />
      <AIRunLogSection />
      <SchedulerLogsSection />
      <DataSourcesSection />
      <AppSection />
    </div>
  );
}

// ── Users sub-tab (owner only) ───────────────────────────────────────────────
function UsersContent() {
  return (
    <div>
      <UsersTab />
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────
export default function SettingsTab({ initialSub }) {
  const { isOwner } = useUser();

  const visibleTabs = SETTINGS_TABS.filter(t => !t.ownerOnly || isOwner);

  const [sub, setSub] = useState(initialSub || 'account');

  useEffect(() => {
    if (initialSub) setSub(initialSub);
  }, [initialSub]);

  // If current sub-tab is owner-only but user is not owner, fall back to account
  const activeSub = (SETTINGS_TABS.find(t => t.id === sub)?.ownerOnly && !isOwner)
    ? 'account'
    : sub;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', paddingTop: 8 }}>

      {/* Sub-tab pill nav */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap',
        borderBottom: '.5px solid var(--hair)', paddingBottom: 0,
      }}>
        {visibleTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: activeSub === t.id ? 700 : 400,
              color: activeSub === t.id ? 'var(--ink)' : 'var(--ink-3)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeSub === t.id ? '2px solid var(--amber-ink)' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              transition: 'color .12s',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      <div>
        {activeSub === 'account' && <AccountContent />}
        {activeSub === 'data'    && isOwner && <DataContent />}
        {activeSub === 'system'  && isOwner && <SystemContent />}
        {activeSub === 'users'   && isOwner && <UsersContent />}
      </div>
    </div>
  );
}
